import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AMPageShell from './components/AMPageShell';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import Icon from 'components/AppIcon';
import { ASSETS } from './data/assetData';

const OUTCOME_GROUPS = [
  { key: 'Confirmed', label: 'Confirmed', icon: 'CheckCircle', colour: 'var(--color-success)', bg: 'bg-success/5 border-success/20' },
  { key: 'Confirmed with actions', label: 'Confirmed with Actions', icon: 'ClipboardCheck', colour: 'var(--color-primary)', bg: 'bg-primary/5 border-primary/20' },
  { key: 'Watchlist', label: 'Watchlist', icon: 'AlertCircle', colour: 'var(--color-warning)', bg: 'bg-warning/5 border-warning/20' },
  { key: 'Escalated', label: 'Escalated', icon: 'AlertTriangle', colour: 'var(--color-error)', bg: 'bg-error/5 border-error/20' },
  { key: 'Exit case', label: 'Exit Case', icon: 'LogOut', colour: 'var(--color-primary)', bg: 'bg-primary/10 border-primary/30' },
];

const CADENCE_OPTIONS = ['monthly', 'quarterly', 'six monthly', 'annual'];

export default function WatchlistActions({ selectedAssets, showToast }) {
  const assets = selectedAssets || ASSETS;
  const navigate = useNavigate();
  const [cadences, setCadences] = useState(
    Object.fromEntries(assets?.map((a) => [a?.id, a?.monitoringCadence || 'quarterly']))
  );

  const handleCadenceChange = (id, value) => {
    setCadences((p) => ({ ...p, [id]: value }));
    showToast?.(`Monitoring cadence updated`);
  };

  return (
    <AMPageShell activeTab="/asset-management/watchlist-actions">
      <div className="space-y-6">
        {OUTCOME_GROUPS?.map((group) => {
          const groupAssets = assets?.filter((a) => a?.watchlistStatus === group?.key);
          if (groupAssets?.length === 0) return null;
          return (
            <div key={group?.key} className={`border rounded-lg overflow-hidden ${group?.bg}`}>
              <div className="px-6 py-4 border-b border-inherit flex items-center gap-2">
                <Icon name={group?.icon} size={16} color={group?.colour} strokeWidth={2} />
                <h2 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>{group?.label}</h2>
                <span className="ml-auto font-data text-sm font-semibold" style={{ color: group?.colour }}>{groupAssets?.length}</span>
              </div>
              <div className="divide-y divide-border/50">
                {groupAssets?.map((asset) => (
                  <div key={asset?.id} className="px-6 py-4 bg-card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <button
                            onClick={() => navigate(`/portfolio/assets/${asset?.id}`, { state: { from: 'asset-management', backPath: '/asset-management/watchlist', backLabel: 'Watchlist & Actions' } })}
                            className="font-caption text-sm font-semibold text-primary hover:underline"
                          >
                            {asset?.name}
                          </button>
                          <ReviewStatusBadge status={asset?.reviewStatus} />
                          <span className="font-caption text-xs text-muted-foreground">{asset?.sector} · {asset?.country}</span>
                        </div>
                        <p className="font-body text-sm text-foreground">{asset?.situationUpdate}</p>
                        {asset?.agreedActions && (
                          <p className="font-caption text-xs text-muted-foreground mt-1"><span className="font-medium">Actions:</span> {asset?.agreedActions}</p>
                        )}
                        {group?.key === 'Escalated' && asset?.delegatedAuthorityReason && (
                          <div className="mt-2 p-3 bg-error/5 border border-error/20 rounded-md">
                            <p className="font-caption text-xs font-semibold text-error mb-1">Delegated Authority Reason</p>
                            <p className="font-body text-xs text-foreground">{asset?.delegatedAuthorityReason}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {(group?.key === 'Watchlist' || group?.key === 'Escalated') && (
                          <div className="flex items-center gap-2">
                            <span className="font-caption text-xs text-muted-foreground">Monitoring:</span>
                            <select
                              value={cadences?.[asset?.id] || 'quarterly'}
                              onChange={(e) => handleCadenceChange(asset?.id, e?.target?.value)}
                              className="text-xs px-2 py-1 border border-border rounded bg-card text-foreground font-caption"
                            >
                              {CADENCE_OPTIONS?.map((c) => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
                            </select>
                          </div>
                        )}
                        <a
                          href={`/portfolio/assets/${asset?.id}`}
                          className="text-xs text-primary hover:underline font-caption flex items-center gap-1"
                        >
                          <Icon name="FileText" size={12} color="var(--color-primary)" strokeWidth={2} />
                          View Review Pack
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AMPageShell>
  );
}
